/**
 * @title The add dialog - image upload
 * @description The component to upload images of tradeins
 */
import {makeStyles} from "@mui/styles";
import {CameraIcon, CloseIcon} from "../../../../svg/SvgIcons";
import * as React from "react";
import {ChangeEvent, FC} from "react";
import clsx from "clsx";
import {GlobalLayout, StyledTypography, UploadImageBox} from "../../../../providers/layout/LayoutProvider";
import {Box, LinearProgress, styled, useMediaQuery} from "@mui/material";
import {UploadService} from "../../../../components/UploadService";
import {useRecoilValue} from "recoil";
import {AccessContextData} from "../../../../providers/access/AppContextProvider";
import {LinearProgressProps} from "@mui/material/LinearProgress/LinearProgress";
import {BackgroundImage} from "../../../../components/ImageLoader";
import {LocalizationsAtom} from "../../../../providers/localization/LocalizationProvider";
import {Analytics, AnalyticsEvent} from "../../../../analytics/Analytics";
import {ImageServerUrl} from "../../../../Settings";
import {PhotoClient} from "@michaeltoft/photoclient";

type ImageUploadProps = {
   imageId?: string
   enabled: boolean
   hidden?: boolean
   onUpdate: (imageId?: string) => void
}

export const ImageUpload: FC<ImageUploadProps> = ({enabled, hidden, onUpdate, imageId}) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const appContext = useRecoilValue(AccessContextData);
   const butRef = React.useRef<HTMLInputElement | null>(null);
   const [progress, setProgress] = React.useState<number | null>(0);
   const classes = useImageUploadStyles();
   const globalLayout = useRecoilValue(GlobalLayout);
   const isDesktop = useMediaQuery('(min-width:800px)');

   const uploadService = UploadService(ImageServerUrl, appContext.shopId);

   const selectFile = async (event: ChangeEvent<any>) => {
      if (!enabled) return;

      setProgress(1);
      let length = event.target?.files?.length || 0;
      for (let i = 0; i < length; i++) {
         let file = event.target?.files?.item(i);
         setProgress(1);
         let axiosResponse = await uploadService(file!, event1 => {
            setProgress(Math.min(100, Math.round(((event1.loaded * 100) / event1.total))));
            if (event1.loaded === event1.total) {
               setTimeout(() => {
                  setProgress(null);
               }, 500);
            }
         });

         Analytics.event(AnalyticsEvent.trackItemImage, {
            fileBytes: String(file?.size || -1),
            fileName: file?.name
         });

         if (axiosResponse.success) {
            onUpdate(axiosResponse.imageId);
         } else {
            // not good
            onUpdate(undefined);
         }
      }
   }

   const onRemoveImage = () => {
      onUpdate(undefined);
   }

   function isTouchDevice() {
      return (('ontouchstart' in window) ||
         (navigator.maxTouchPoints > 0) ||
         ((navigator as any).msMaxTouchPoints > 0));
   }

   if (hidden) {
      return <div className={classes.imageUpload}>&nbsp;</div>
   } else if (imageId) {
      return <UploadImageBox disabled={false} className={clsx(classes.imageUpload, 'c2s-upload-container')}>
         {(progress || 0) > 0 &&
            <BorderLinearProgress style={{color: globalLayout.widgets?.selectionColor || '#000000'}} color={"inherit"}
                                  className={classes.progress} variant={"determinate"} value={progress || 0}/>}
         <BackgroundImage src={ImageServerUrl + '/' + imageId}
                          className={classes.uploadImage}
                          title="Product image"
         />
         <div className={classes.deleteImage} onClick={onRemoveImage}>
            <CloseIcon iconcolor={globalLayout.widgets?.actionItemBorderColor || '#FF5376'}/>
         </div>
      </UploadImageBox>
   } else {
      return <Box sx={{
         display: 'flex',
         gap: '20px',
         justifyContent: 'center',
         flexWrap: 'wrap'
      }}>
         <label htmlFor={"btn-upload"} style={{position: 'relative', cursor: 'pointer'}} onTouchStart={(e) => {
            // Introduced because iPhone / Chrome and Safari on some storefronts do not respond to single-taps
            // https://css-tricks.com/annoying-mobile-double-tap-link-issue/
            // If desktop browsers some day
            if (isTouchDevice()) {
               e.stopPropagation();
               e.preventDefault()
               butRef.current?.click();
            }
         }}>
            {(progress || 0) > 0 &&
               <BorderLinearProgress style={{color: globalLayout.widgets?.selectionColor || '#000000'}}
                                     color={"inherit"}
                                     className={classes.progress} variant={"determinate"} value={progress || 0}/>}
            <UploadImageBox disabled={!enabled} className={clsx(classes.imageUpload, 'c2s-upload-container')}>
               <div className={clsx(classes.imageIcon, 'c2s-uploadicon-container')}>
                  <CameraIcon
                     iconcolor={enabled ? (globalLayout.widgets?.uploadPhoto?.color || '#FF5376') : (globalLayout.widgets?.uploadPhoto?.disabledColor || '#F0F0F0')}/>
               </div>
               <div className={classes.imageText}>
                  <StyledTypography type={"main"} variant={"subtitle1"}
                                    className={clsx(!enabled && classes.disabledText, classes.centerText)}>
                     <input
                        disabled={!enabled}
                        id="btn-upload"
                        name="btn-upload"
                        style={{display: 'none'}}
                        type="file"
                        accept={"image/*"}
                        onChange={selectFile}
                        ref={butRef}
                     />
                     {localizations.imageUploadText}
                  </StyledTypography>
               </div>
            </UploadImageBox>
         </label>
         {isDesktop &&
            <UploadImageBox disabled={!enabled} className={clsx(classes.imageUpload, 'c2s-upload-container')} sx={{
               width: "120px",
               display: 'flex',
               justifyContent: 'center', // Centers horizontally
               alignItems: 'center',     // Centers vertically
            }}>
               <PhotoClient
                  photoIdent={appContext.consumerId}
                  apiKey={"YxtZykjlQHpt7dWmtj2hr9tPwpxLa_Y9fn-17VF_5nnQAzFus4xeWQ=="}
                  size={110}
                  receiverId={"trn"}
                  pollingInterval={3000}
                  params={{
                     shopId: appContext.shopId
                  }}
                  logo={"https://tradein.create2stay.com/phone.png"}
                  logoSize={28}
                  logoOpacity={0.6}

                  onImageUploaded={(photoResponse) => {
                     onUpdate(photoResponse[photoResponse.length - 1].result.data.imageId);
                  }}/>
            </UploadImageBox>
         }
      </Box>
   }
}

const useImageUploadStyles = makeStyles({
   imageUpload: {
      width: '120px',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      cursor: 'pointer'
   },
   progress: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      height: '20px'
   },
   enableBorder: {
      border: '2px dashed #FF5376',
   },
   disableBorder: {
      border: '2px dashed #e0e0e0',
   },
   imageText: {
      fontWeight: 'bold'
   },
   imageIcon: {
      width: '32px',
      height: '32px',
      borderRadius: '2px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
   },
   uploadImage: {
      height: '84px',
      width: '90px'
   },
   deleteImage: {
      position: 'absolute',
      right: 0,
      top: 0,
      cursor: 'pointer'
   },
   disabledText: {},
   centerText: {
      textAlign: 'center'
   }
});


const BorderLinearProgress = styled(LinearProgress)<LinearProgressProps>(({theme, variant}) => {
   return {
      root: {
         height: 10,
         borderRadius: 5,
      },
      bar: {
         borderRadius: 5,
         backgroundColor: '#a0a0a0',
      }
   }
});
